/**
 * "Feedback on this build" for accounts with end-to-end encryption. The relay's plaintext
 * `/api/builds/:id/feedback` route can no longer read feedback text, so routing happens here, on
 * the decrypted build report and private work entities: the feedback becomes an encrypted ready
 * task in the workstream that owns the shipped work (its landing triggers the next build), or an
 * encrypted idea for triage when the owner is unclear. Mirrors apps/daemon/src/release/feedback.ts.
 */
import type { Api } from './api.js'
import type { BuildCard, TaskDetail } from './types.js'

type FeedbackApi = Pick<Api, 'build' | 'task' | 'createTask' | 'createIdea'>

export interface BuildFeedbackBody {
  text: string
  taskId?: string
  attachmentIds?: string[]
}

export interface BuildFeedbackResult {
  feedbackId: string
  status: 'start-session' | 'triage'
  taskId: string | null
  ideaId: string | null
}

const STOP = new Set(
  'the and for are but not you this that with from have has was were its too very just can will should would could into onto than then there their they them when what which while also still more less some any all our out off per via'.split(
    ' ',
  ),
)

export function feedbackTokens(text: string): Set<string> {
  const out = new Set<string>()
  for (const raw of text.toLowerCase().match(/[a-z0-9]+/g) ?? []) {
    if (raw.length < 3 || STOP.has(raw)) continue
    out.add(raw.length > 4 ? raw.replace(/(ing|ed|es|s)$/, '') : raw)
  }
  return out
}

export function composeFeedback(build: BuildCard, tasks: TaskDetail[], text: string): string {
  const shipped = tasks.length
    ? `\nShipped tasks it may concern: ${tasks.map((t) => `"${t.title}" [${t.id}]`).join(', ')}`
    : ''
  return `Feedback on ${build.title}${build.version ? ` (version ${build.version})` : ''}:${shipped}\n\n${text.trim()}`
}

function overlap(a: Set<string>, b: Set<string>): number {
  let n = 0
  for (const t of a) if (b.has(t)) n++
  return n
}

/** Pure choice of the owning workstream among the build's tasks; null when unclear. */
export function chooseOwner(text: string, tasks: TaskDetail[]): string | null | undefined {
  const byWs = new Map<string | null, TaskDetail[]>()
  for (const t of tasks) byWs.set(t.workstreamId, [...(byWs.get(t.workstreamId) ?? []), t])
  if (byWs.size === 1) return [...byWs.keys()][0]
  const words = feedbackTokens(text)
  const ranked = [...byWs.entries()]
    .map(([ws, ts]) => ({
      ws,
      score: overlap(words, feedbackTokens(ts.map((t) => t.title).join(' '))),
    }))
    .sort((a, b) => b.score - a.score)
  const [best, second] = ranked
  // Route only on a clear winner; otherwise one triage idea beats a task in the wrong place.
  if (best && best.score > 0 && best.score >= 2 * (second?.score ?? 0)) return best.ws
  return undefined
}

export async function routeBuildFeedback(
  api: FeedbackApi,
  buildId: string,
  body: BuildFeedbackBody,
): Promise<BuildFeedbackResult> {
  const build = await api.build(buildId)
  const ids = new Set<string>([
    ...build.taskIds,
    ...build.tasks.flatMap((t) => (t.taskId ? [t.taskId] : [])),
  ])
  if (body.taskId) {
    ids.clear()
    ids.add(body.taskId)
  }
  const tasks: TaskDetail[] = []
  for (const id of ids) {
    const t = await api.task(id).catch(() => undefined)
    if (t && t.projectId === build.projectId) tasks.push(t)
  }
  const description = composeFeedback(build, tasks, body.text)
  const owner = tasks.length ? chooseOwner(body.text, tasks) : undefined
  if (owner === undefined) {
    const idea = await api.createIdea({
      text: description,
      projectId: build.projectId,
      mode: 'idea',
    })
    return { feedbackId: idea.id, status: 'triage', taskId: null, ideaId: idea.id }
  }
  const first = body.text.trim().split(/\r?\n/)[0] ?? ''
  const task = await api.createTask({
    projectId: build.projectId,
    ...(owner ? { workstreamId: owner } : {}),
    title: `Feedback: ${first.length > 80 ? `${first.slice(0, 79)}…` : first}`,
    description,
    labels: ['feedback', `build:${build.buildId}`],
    priority: 2,
  })
  return { feedbackId: task.id, status: 'start-session', taskId: task.id, ideaId: null }
}

/** Replace the plaintext relay route; late-bound so it always uses the private work API. */
export function installBuildFeedback(api: Api): void {
  api.buildFeedback = (id, body) => routeBuildFeedback(api, id, body)
}
